/**
 * viewedItems.ts - 广场/订阅页面共享的已查看状态管理
 *
 * 使用 localStorage 存储用户查看过的 item 及其查看时间
 * 广场和订阅页面共享同一份数据，实现统一的"更新红点"逻辑
 */

const STORAGE_KEY = 'square_viewed_items';

interface ViewedItemsMap {
  [itemId: string]: number; // timestamp of last view
}

/**
 * 获取所有已查看项目的记录
 */
export function getViewedItems(): ViewedItemsMap {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // ignore
  }
  return {};
}

/**
 * 标记某个 item 为已查看
 * @param itemId 项目 ID
 */
export function markAsViewed(itemId: string): void {
  try {
    const items = getViewedItems();
    items[itemId] = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // ignore
  }
}

/**
 * 获取某个 item 的最后查看时间
 * @param itemId 项目 ID
 * @returns 时间戳，如果从未查看过则返回 null
 */
export function getLastViewedTime(itemId: string): number | null {
  const items = getViewedItems();
  return items[itemId] || null;
}

/**
 * 检查某个 item 是否有更新（用于显示红点）
 * @param itemId 项目 ID
 * @param itemUpdatedAt 项目的最后更新时间
 * @returns 如果有更新返回 true
 */
export function hasUpdate(itemId: string, itemUpdatedAt: Date | string | null): boolean {
  if (!itemUpdatedAt) return false;

  const lastViewed = getLastViewedTime(itemId);
  if (lastViewed === null) {
    // 从未查看过，视为有更新
    return true;
  }

  const updatedTime = typeof itemUpdatedAt === 'string'
    ? new Date(itemUpdatedAt).getTime()
    : itemUpdatedAt.getTime();

  return updatedTime > lastViewed;
}

/**
 * 清除某个 item 的查看记录
 * @param itemId 项目 ID
 */
export function clearViewedItem(itemId: string): void {
  try {
    const items = getViewedItems();
    delete items[itemId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // ignore
  }
}

/**
 * 清除所有查看记录
 */
export function clearAllViewedItems(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}
